import { Component, input, signal, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faLock } from '@fortawesome/pro-regular-svg-icons';
import { SidebarItem } from './sidebar.types';
import { TooltipDirective } from '../tooltip/tooltip.directive';

// =============================================================================
// Sidebar Nav Item Component
// Recursive component that renders a single navigation item and its children.
// Supports arbitrary nesting depth via self-reference.
// =============================================================================
@Component({
  selector: 'ams-sidebar-nav-item',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    FontAwesomeModule,
    SidebarNavItemComponent,
    TooltipDirective,
  ],
  styleUrl: './sidebar.component.scss',
  template: `
    <div class="ams-sidebar__item-wrapper">
      @if (item().route && !item().children?.length) {
        @if (item().disabled) {
          <!-- Leaf node: locked (feature-gated) — non-navigable, lock + tooltip -->
          <div
            class="ams-sidebar__item ams-sidebar__item--disabled ams-sidebar__item--locked"
            [class.ams-sidebar__item--child]="depth() > 0"
            [style.--nav-depth]="depth()"
            [style.display]="sidebarCollapsed() && depth() > 0 ? 'flex' : null"
            [style.padding]="sidebarCollapsed() && depth() > 0 ? 'var(--ams-space-3) var(--ams-space-3) var(--ams-space-3) calc(var(--ams-space-3) - ' + (depth() - 1) * 4 + 'px)' : null"
            [amsTooltip]="item().lockMessage || item().label"
            amsTooltipPosition="right"
          >
            @if (item().icon) {
              <span class="ams-sidebar__item-icon">
                <fa-icon [icon]="item().icon!" [size]="depth() === 0 ? 'lg' : 'sm'"></fa-icon>
              </span>
            }
            @if (!sidebarCollapsed()) {
              <span class="ams-sidebar__item-label">{{ item().label }}</span>
              <span class="ams-sidebar__item-lock">
                <fa-icon [icon]="faLock" size="xs"></fa-icon>
              </span>
            }
          </div>
        } @else {
          <!-- Leaf node: routable link -->
          <a
            class="ams-sidebar__item"
            [class.ams-sidebar__item--child]="depth() > 0"
            [style.--nav-depth]="depth()"
            [style.display]="sidebarCollapsed() && depth() > 0 ? 'flex' : null"
            [style.padding]="sidebarCollapsed() && depth() > 0 ? 'var(--ams-space-3) var(--ams-space-3) var(--ams-space-3) calc(var(--ams-space-3) - ' + (depth() - 1) * 4 + 'px)' : null"
            [routerLink]="item().route"
            routerLinkActive="ams-sidebar__item--active"
            [routerLinkActiveOptions]="{ exact: item().matchPrefix ? false : !item().children?.length }"
            [attr.title]="sidebarCollapsed() ? item().label : null"
          >
            @if (item().icon) {
              <span class="ams-sidebar__item-icon">
                <fa-icon [icon]="item().icon!" [size]="depth() === 0 ? 'lg' : 'sm'"></fa-icon>
              </span>
            }
            @if (!sidebarCollapsed()) {
              <span class="ams-sidebar__item-label">{{ item().label }}</span>
              @if (item().badge) {
                <span class="ams-sidebar__item-badge">{{ item().badge }}</span>
              }
            }
          </a>
        }
      } @else if (item().children?.length) {
        <!-- Parent: expandable row + recursive children -->
        <div
          class="ams-sidebar__item ams-sidebar__item--expandable"
          [class.ams-sidebar__item--child]="depth() > 0"
          [class.ams-sidebar__item--expanded]="isExpanded()"
          [class.ams-sidebar__item--active]="isItemRouteActive()"
          [class.ams-sidebar__item--disabled]="item().disabled"
          [style.padding-left]="!sidebarCollapsed() && depth() > 0 ? 'calc(var(--ams-space-3) + ' + depth() + ' * 8px - 5px)' : null"
          [style.display]="sidebarCollapsed() && depth() > 0 ? 'flex' : null"
          [style.padding]="sidebarCollapsed() && depth() > 0 ? 'var(--ams-space-3) var(--ams-space-3) var(--ams-space-3) calc(var(--ams-space-3) - ' + (depth() - 1) * 4 + 'px)' : null"
          [attr.title]="sidebarCollapsed() ? item().label : null"
          (click)="toggle()"
        >
          @if (item().route) {
            <a
              class="ams-sidebar__item-link"
              [routerLink]="item().route"
              [attr.title]="sidebarCollapsed() ? item().label : null"
            >
              @if (item().icon) {
                <span class="ams-sidebar__item-icon">
                  <fa-icon [icon]="item().icon!" [size]="depth() === 0 ? 'lg' : 'sm'"></fa-icon>
                </span>
              }
              @if (!sidebarCollapsed()) {
                <span class="ams-sidebar__item-label">{{ item().label }}</span>
              }
            </a>
          } @else {
            @if (item().icon) {
              <span class="ams-sidebar__item-icon">
                <fa-icon [icon]="item().icon!" [size]="depth() === 0 ? 'lg' : 'sm'"></fa-icon>
              </span>
            }
            @if (!sidebarCollapsed()) {
              <span class="ams-sidebar__item-label">{{ item().label }}</span>
            }
          }
          @if (!sidebarCollapsed()) {
            <button type="button" class="ams-sidebar__item-chevron-button">
              <span class="ams-sidebar__item-chevron">
                <fa-icon [icon]="faChevronDown" size="sm"></fa-icon>
              </span>
            </button>
          }
        </div>

        <!-- Always rendered so grid-template-rows can animate 0fr ↔ 1fr.
             Inner wrapper has overflow:hidden to clip during the transition. -->
        <div
          class="ams-sidebar__submenu"
          [class.ams-sidebar__submenu--open]="isOpen()"
          [class.ams-sidebar__submenu--no-guide]="item().hideSubmenuGuide"
          [style.margin-left]="sidebarCollapsed() ? (depth() * 4) + 'px' : null"
          [style.padding]="sidebarCollapsed() ? '0' : null"
        >
          <div class="ams-sidebar__submenu-inner">
            @for (child of item().children; track child.id) {
              <ams-sidebar-nav-item
                [item]="child"
                [sidebarCollapsed]="sidebarCollapsed()"
                [depth]="depth() + 1"
              />
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class SidebarNavItemComponent {
  private readonly router = inject(Router);

  readonly item = input.required<SidebarItem>();
  readonly sidebarCollapsed = input<boolean>(false);
  readonly depth = input<number>(0);

  readonly faChevronDown = faChevronDown;
  readonly faLock = faLock;
  readonly isExpanded = signal<boolean>(false);
  readonly activeUrl = signal<string>(this.router.url.split('?')[0]);

  readonly isOpen = computed(
    () =>
      this.isExpanded() ||
      (this.sidebarCollapsed() && (this.isActiveParent() || this.isItemRouteActive())),
  );

  readonly isActiveParent = computed(() =>
    this.hasActiveChild(this.item(), this.activeUrl())
  );
  readonly isItemRouteActive = computed(() => {
    const route = this.item().route;
    if (!route) return false;

    const currentUrl = this.activeUrl();
    return currentUrl === route || (route !== '/' && currentUrl.startsWith(`${route}/`));
  });

  constructor() {
    // Keep activeUrl in sync with navigation so isActiveParent updates reactively
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.activeUrl.set(e.urlAfterRedirects.split('?')[0]);
      }
    });

    effect(() => {
      if (this.sidebarCollapsed()) {
        this.isExpanded.set(false);
      } else {
        if (this.isItemRouteActive() || this.hasActiveChild(this.item(), this.activeUrl())) {
          this.isExpanded.set(true);
        }
      }
    });
  }

  private hasActiveChild(item: SidebarItem, url: string): boolean {
    if (!item.children?.length) return false;
    return item.children.some(child =>
      (child.route && url.startsWith(child.route) && child.route !== '/') ||
      this.hasActiveChild(child, url)
    );
  }

  toggle(): void {
    this.isExpanded.update((v) => !v);
  }
}
